import React, { useEffect, useState } from 'react';
import { Button, Card, Checkbox, Divider, FormLayout, Layout, Select, Text, TextField, Toast, Tooltip } from "@shopify/polaris";
import { useSelector } from "react-redux";
import macBookImg from "../../assets/img/macbook-1.png";
import CurrencyPreview from "../CurrencyPreview";
import { currencyListOptions } from "../../helper/currencyList";
import { GetApiCall } from "../../helper/axios";

const LocationDesignSettings = ({ formik, changeTextFn }) => {
    const { store_data } = useSelector((store) => store.commonData);
    const token = store_data?.token;
    const isExistingCustomer = !!store_data?.isExistingCustomer; // only existing customers see the "New" badge
    const [activeSection, setActiveSection] = useState(null);
    const [detecting, setDetecting] = useState(false);
    // "New" badge on the Theme Adaptation section: show until the merchant has
    // opened the Design tab once (tracked per-browser, shared key with the tabs).
    const [showThemeNew] = useState(() => {
        try {
            const t = JSON.parse(window.localStorage.getItem("hw_features_seen_v2") || "{}").themeAdaptation;
            return t ? (Date.now() - t) < 7 * 24 * 60 * 60 * 1000 : true;
        } catch (e) { return true; }
    });
    useEffect(() => {
        try {
            const seen = JSON.parse(window.localStorage.getItem("hw_features_seen_v2") || "{}") || {};
            if (!seen.themeAdaptation) {
                seen.themeAdaptation = Date.now();
                window.localStorage.setItem("hw_features_seen_v2", JSON.stringify(seen));
            }
        } catch (e) { /* ignore */ }
    }, []);
    const [activeToast, setActiveToast] = useState(false);
    const [activeToastMessage, setActiveToastMessage] = useState('');

    // Theme Adaptation Engine - "auto-match my theme".
    const autoMatchTheme = async () => {
        setDetecting(true);
        try {
            const res = await GetApiCall('GET', '/theme/detect', { authentication: token });
            const t = res?.data?.data;
            if (res?.data?.statusCode === 200 && t) {
                if (t.backgroundColor) changeTextFn('backgroundColor', t.backgroundColor);
                if (t.textColor) changeTextFn('textColor', t.textColor);
                if (t.borderColor) changeTextFn('borderLineColor', t.borderColor);
                if (t.hoverBackgroundColor) changeTextFn('hoverBackgroundColor', t.hoverBackgroundColor);
                if (t.textHoverColor) changeTextFn('textHoverColor', t.textHoverColor);
                if (t.borderRadius) changeTextFn('borderRadius', t.borderRadius);
                setActiveToast(true);
                setActiveToastMessage(t.detected
                    ? "Matched your theme! Review the preview and Save."
                    : "Applied sensible defaults (theme could not be read). Review and Save.");
            } else {
                setActiveToast(true);
                setActiveToastMessage("Could not detect your theme. Please try again.");
            }
        } catch (e) {
            setActiveToast(true);
            setActiveToastMessage("Could not detect your theme. Please try again.");
        } finally {
            setDetecting(false);
        }
    };

    const selectLocationoptions = [
        { label: 'Fixed Position', value: 0 },
        { label: 'Custom Placement', value: 1 },
        { label: "Don't Show at all", value: 2 },
    ];

    let priceFormatOptions = [
        { label: 'With Currency', value: 0 },
        { label: 'Without Currency', value: 1 }
    ]

    let displayFlagOptions = [
        { label: 'Yes', value: 0 },
        { label: 'No', value: 1 }
    ]

    let flagStyleOption = [
        { label: 'Rectangle', value: 0 },
        { label: 'Circle', value: 1 }
    ]

    const handleSectionToggle = (section) => {
        setActiveSection(activeSection === section ? null : section);
    };

    const toggleActive = () => {
        setActiveToast((activeToast) => !activeToast)
    };

    const toastMarkup = activeToast ? (
        <Toast content={activeToastMessage} onDismiss={toggleActive} />
    ) : null;

    const copytoClipboardFn = (content) => {
        navigator.clipboard.writeText(content);
        setActiveToast(true);
        setActiveToastMessage("Copied Successfully!");
    };

    return (
        <>
            <div className="mt-20">
                <Layout>
                    <Layout.AnnotatedSection
                        id="theme-adaptation"
                        title={
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                                Theme Adaptation
                                {(showThemeNew && isExistingCustomer) ? (
                                    <>
                                        <span style={{ background: "#000", color: "#fff", fontSize: 12, fontWeight: 500, padding: "1px 8px", borderRadius: 8, lineHeight: 1.5 }}>New</span>
                                        <Tooltip content="Automatically match the currency switcher's colors, fonts, and corner style to your store's theme." dismissOnMouseOut>
                                            <span role="img" aria-label="More info" style={{ display: "inline-flex", alignItems: "center", cursor: "help", color: "#8a8a8a" }}>
                                                <svg width="15" height="15" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path d="M10 1.5a8.5 8.5 0 100 17 8.5 8.5 0 000-17zm0 15.5a7 7 0 110-14 7 7 0 010 14zM9 9h2v5H9V9zm0-3h2v2H9V6z" /></svg>
                                            </span>
                                        </Tooltip>
                                    </>
                                ) : null}
                            </span>
                        }
                        description="Make the currency selector match your store automatically. Auto-match reads your theme's colors and corner radius; the selector also inherits your theme's font so it looks native with zero setup."
                    >
                        <Card>
                            <FormLayout>
                                <Checkbox
                                    label="Automatically adapt the selector to my theme"
                                    checked={!!formik.values.autoThemeAdaptation}
                                    onChange={(value) => {
                                        changeTextFn('autoThemeAdaptation', value);
                                        // Turning it OFF discards the fetched theme colors and
                                        // restores the app's normal defaults.
                                        if (!value) {
                                            const defaults = {
                                                borderLineColor: '#cccccc',
                                                textColor: '#000000',
                                                textHoverColor: '#000000',
                                                backgroundColor: '#f4f4f4',
                                                hoverBackgroundColor: '#f4f4f4',
                                                borderRadius: '8px',
                                                fontSize: '',
                                            };
                                            Object.keys(defaults).forEach((k) => changeTextFn(k, defaults[k]));
                                        }
                                    }}
                                    helpText="The selector inherits your theme's font and uses theme-matched colors and corner radius. Unchecking restores the app's default styling. You can still override anything below."
                                />
                                <Button onClick={autoMatchTheme} loading={detecting} variant="primary">
                                    Auto-match my theme
                                </Button>
                            </FormLayout>
                        </Card>
                    </Layout.AnnotatedSection>
                </Layout>
            </div>
            <div className="mt-20">
                <Layout>
                    <Layout.AnnotatedSection
                        id="switcher-location"
                        title="Switcher Location"
                        description="Choose where you want the currency switcher to appear in your store. You can place it in the header, footer, or on specific pages like the product page, cart page, or checkout page."
                    >
                        <Card>
                            <Select
                                label="Select Location"
                                options={selectLocationoptions}
                                onChange={(value) => {
                                    changeTextFn('switcherLocation', selectLocationoptions.find(options => options.value === Number(value)))
                                }}
                                value={formik.values.switcherLocation?.value}
                            />
                            <div className='space'></div>
                            <Text fontWeight="regular" variant="headingSm" as={'span'}>
                                {formik.values.switcherLocation?.value === 0 ? 'Switcher will be fixed always at a selected position even on page scroll.' :
                                    formik.values.switcherLocation?.value === 1 ? "The currency switcher will be integrated into your store's header navigation, making it easily accessible while browsing." :
                                        formik.values.switcherLocation?.value === 2 ? "The currency switcher will be hidden from your store. Customers will only see prices in your store's default currency." : ""
                                }
                            </Text>
                            <div className='space'></div>
                            {formik.values.switcherLocation?.value === 0 ?
                                <div className="hw-currencyLocation--main-div">
                                    <img src="https://cdn.shopify.com/s/files/1/0601/7908/7425/files/Apple_MacBook_Air.png?v=1745401042" />
                                    <div className='top-left'>
                                        <input onClick={() => changeTextFn('selectFixedPosition', 0)}
                                            type="checkbox" name="positionTopLeftChecked"
                                            checked={formik.values.selectFixedPosition === 0} />
                                    </div>
                                    <div className='top-right'>
                                        <input onClick={() => changeTextFn('selectFixedPosition', 1)}
                                            type="checkbox" name="positionTopRightChecked"
                                            checked={formik.values.selectFixedPosition === 1} />
                                    </div>
                                    <div className='bottom-left'>
                                        <input onClick={() => changeTextFn('selectFixedPosition', 2)}
                                            type="checkbox" name="positionBottomLeftChecked"
                                            checked={formik.values.selectFixedPosition === 2} />
                                    </div>
                                    <div className='bottom-right'>
                                        <input onClick={() => changeTextFn('selectFixedPosition', 3)}
                                            type="checkbox" name="positionBottomRightChecked"
                                            checked={formik.values.selectFixedPosition === 3} />
                                    </div>

                                </div> :
                                formik.values.switcherLocation?.value === 1 ?
                                    <>
                                        <div style={{ marginBottom: '20px', padding: '15px', background: '#f6f6f7', borderRadius: '4px' }}>
                                            <Text variant="bodyMd" fontWeight="medium">Steps to add currency dropdown in header:</Text>
                                            <div style={{ marginTop: '10px' }}>

                                                <div style={{ marginBottom: '8px' }}>
                                                    <Text as="span" variant="bodyMd">
                                                        1. Go to <strong>Sales Channels</strong> → <strong>Online Stores</strong> → <strong>Themes</strong>
                                                    </Text>
                                                </div>

                                                <div style={{ marginBottom: '8px' }}>
                                                    <Text as="span" variant="bodyMd">
                                                        2. Click on three dots (⋮) of your selected theme → Select <strong>Edit code</strong>
                                                    </Text>
                                                </div>

                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '10px 0 10px 20px' }}>
                                                    <Text variant="bodyMd" fontWeight="medium">For all devices:</Text>
                                                    <div style={{
                                                        background: '#ffffff',
                                                        padding: '10px',
                                                        borderRadius: '4px',
                                                        border: '1px solid #d8d8d8',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        gap: '10px',
                                                        width: '30vw',
                                                    }}>
                                                        <Text as="span" variant="bodySm" style={{ whiteSpace: 'nowrap' }}>
                                                            {`<div id="hw-currency-custom-placement"></div>`}
                                                        </Text>
                                                        <div onClick={() => copytoClipboardFn('<div id="hw-currency-custom-placement"></div>')} style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            cursor: 'pointer',
                                                            color: 'var(--p-color-text-link)',
                                                            whiteSpace: 'nowrap',
                                                        }}>
                                                            <span>Copy</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div style={{ display: 'flex', alignItems: 'center', gap: '2.2vw', margin: '10px 0 10px 20px' }}>
                                                    <Text variant="bodyMd" fontWeight="medium">For mobile:</Text>
                                                    <div style={{
                                                        background: '#ffffff',
                                                        padding: '10px',
                                                        borderRadius: '4px',
                                                        border: '1px solid #d8d8d8',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        gap: '10px',
                                                        width: '30vw',
                                                    }}>
                                                        <Text as="span" variant="bodySm" style={{ whiteSpace: 'nowrap' }}>
                                                            {`<div id="hw-currency-custom-placement" data-show="mobile"></div>`}
                                                        </Text>
                                                        <div onClick={() => copytoClipboardFn('<div id="hw-currency-custom-placement" data-show="mobile"></div>')} style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            cursor: 'pointer',
                                                            color: 'var(--p-color-text-link)',
                                                            whiteSpace: 'nowrap',
                                                        }}>
                                                            <span>Copy</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div style={{ display: 'flex', alignItems: 'center', gap: '1.8vw', margin: '10px 0 10px 20px' }}>
                                                    <Text variant="bodyMd" fontWeight="medium">For desktop:</Text>
                                                    <div style={{
                                                        background: '#ffffff',
                                                        padding: '10px',
                                                        borderRadius: '4px',
                                                        border: '1px solid #d8d8d8',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        gap: '10px',
                                                        width: '30vw',
                                                    }}>
                                                        <Text as="span" variant="bodySm" style={{ whiteSpace: 'nowrap' }}>
                                                            {`<div id="hw-currency-custom-placement" data-show="desktop"></div>`}
                                                        </Text>
                                                        <div onClick={() => copytoClipboardFn('<div id="hw-currency-custom-placement" data-show="desktop"></div>')} style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            cursor: 'pointer',
                                                            color: 'var(--p-color-text-link)',
                                                            whiteSpace: 'nowrap',
                                                        }}>
                                                            <span>Copy</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div>
                                                    <Text as="span" variant="bodyMd">
                                                        4. Click on <strong>Save</strong> button to apply changes.
                                                    </Text>
                                                </div>

                                            </div>
                                        </div>

                                    </>
                                    :
                                    formik.values.switcherLocation?.value === 2 ?
                                        <div className="disabled-preview">
                                            <div className="disabled-icon">🚫</div>
                                            <Text variant="span">
                                                Currency Switcher is Disabled
                                            </Text>
                                        </div>
                                        : ""
                            }
                        </Card>
                    </Layout.AnnotatedSection>
                </Layout>
            </div>
            <div className="mt-20">
                <Divider borderColor="border" />
            </div>
            <div className="mt-20">
                <Layout.AnnotatedSection
                    id="design-settings"
                    title="Design Settings"
                    description={<>
                        <Text as={'span'}>Customize how currency symbols and flags appear in your store, including their style and position.</Text>
                        <CurrencyPreview
                            priceFormat={formik.values?.priceFormat || 0}
                            displayFlag={formik.values?.displayFlag || 0}
                            flagStyle={formik.values?.flagStyle || 0}
                            displayBorder={formik.values?.displayBorder || 0}
                            borderColor={formik.values?.borderLineColor || '#d8d8d8'}
                            backgroundColor={formik.values?.backgroundColor || '#ffffff'}
                            backgroundHoverColor={formik.values?.hoverBackgroundColor || '#f6f6f7'}
                            textColor={formik?.values?.textColor || '#202223'}
                            textHoverColor={formik?.values?.textHoverColor || '#008060'}
                            selectedCurrencies={formik?.values?.currenciesList}
                            currencies={currencyListOptions}
                            removeDecimals={formik?.values?.removeDecimals}
                            priceRounding={formik?.values?.pricingRound}
                            borderRadius={formik?.values?.borderRadius}
                            fontSize={formik?.values?.fontSize}
                        />
                    </>}
                >
                    <Card padding="400">
                        <FormLayout>
                            <FormLayout.Group condensed>
                                <Select
                                    label="Price Format"
                                    options={priceFormatOptions}
                                    onChange={(value) => {
                                        changeTextFn('priceFormat', priceFormatOptions.find(options => options.value === Number(value)))
                                    }}
                                    value={formik.values.priceFormat?.value}
                                />
                                <Select
                                    label="Display Flag"
                                    options={displayFlagOptions}
                                    onChange={(value) => {
                                        changeTextFn('displayFlag', displayFlagOptions.find(options => options.value === Number(value)))
                                    }}
                                    value={formik.values.displayFlag?.value}
                                />
                            </FormLayout.Group>
                            <FormLayout.Group condensed>
                                <Select
                                    label="Flag Style"
                                    options={flagStyleOption}
                                    onChange={(value) => {
                                        changeTextFn('flagStyle', flagStyleOption.find(options => options.value === Number(value)))
                                    }}
                                    value={formik.values.flagStyle?.value}
                                />
                                <Select
                                    label="Display Border"
                                    options={displayFlagOptions}
                                    onChange={(value) => {
                                        changeTextFn('displayBorder', displayFlagOptions.find(options => options.value === Number(value)))
                                    }}
                                    value={formik.values.displayBorder?.value}
                                />
                            </FormLayout.Group>
                            <div style={{ padding: "10px 10px" }}>
                                <FormLayout.Group condensed>
                                    <Button
                                        variant="secondary"
                                        onClick={() => handleSectionToggle('advanced')}
                                    >
                                        Advanced Customization
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        onClick={() => handleSectionToggle('customCSS')}
                                    >
                                        Custom CSS
                                    </Button>
                                </FormLayout.Group>
                            </div>

                            {activeSection === 'advanced' && (
                                <div className="advance-custimization-main-div">
                                    <FormLayout.Group condensed>
                                        <TextField
                                            label="Border Line"
                                            value={formik.values.borderLineColor}

                                            onChange={(value) => changeTextFn('borderLineColor', value)}
                                            autoComplete="off"
                                            suffix={
                                                <input
                                                    type="color"
                                                    value={formik.values.borderLineColor}
                                                    onChange={(e) => changeTextFn('borderLineColor', e.target.value)}
                                                    className="color-input-textfield"
                                                />
                                            }
                                        />
                                        <TextField
                                            label="Text Color"
                                            value={formik.values.textColor}
                                            onChange={(value) => changeTextFn('textColor', value)}
                                            autoComplete="off"
                                            suffix={
                                                <input
                                                    type="color"
                                                    value={formik.values.textColor}
                                                    onChange={(e) => changeTextFn('textColor', e.target.value)}
                                                    className="color-input-textfield"
                                                />
                                            }
                                        />
                                    </FormLayout.Group>
                                    <FormLayout.Group condensed>
                                        <TextField
                                            label="Text Hover Color"
                                            value={formik.values.textHoverColor}
                                            onChange={(value) => changeTextFn('textHoverColor', value)}
                                            autoComplete="off"
                                            suffix={
                                                <input
                                                    type="color"
                                                    value={formik.values.textHoverColor}
                                                    onChange={(e) => changeTextFn('textHoverColor', e.target.value)}
                                                    className="color-input-textfield"
                                                />
                                            }
                                        />
                                        <TextField
                                            label="Background Color"
                                            value={formik.values.backgroundColor}
                                            onChange={(value) => changeTextFn('backgroundColor', value)}
                                            autoComplete="off"
                                            suffix={
                                                <input
                                                    type="color"
                                                    value={formik.values.backgroundColor}
                                                    onChange={(e) => changeTextFn('backgroundColor', e.target.value)}
                                                    className="color-input-textfield"
                                                />
                                            }
                                        />
                                    </FormLayout.Group>
                                    <FormLayout.Group condensed>
                                        <TextField
                                            label="Background Hover Color"
                                            value={formik.values.hoverBackgroundColor}
                                            onChange={(value) => changeTextFn('hoverBackgroundColor', value)}
                                            autoComplete="off"
                                            suffix={
                                                <input
                                                    type="color"
                                                    value={formik.values.hoverBackgroundColor}
                                                    onChange={(e) => changeTextFn('hoverBackgroundColor', e.target.value)}
                                                    className="color-input-textfield"
                                                />
                                            }
                                        />

                                    </FormLayout.Group>
                                    <FormLayout.Group condensed>
                                        <TextField
                                            label="Corner Radius"
                                            value={formik.values.borderRadius}
                                            onChange={(value) => changeTextFn('borderRadius', value)}
                                            autoComplete="off"
                                            helpText="e.g. 8px, 4px, or 0px"
                                        />
                                        <TextField
                                            label="Font Size"
                                            value={formik.values.fontSize}
                                            onChange={(value) => changeTextFn('fontSize', value)}
                                            autoComplete="off"
                                            placeholder="inherit"
                                            helpText="Leave blank to inherit your theme's size, e.g. 15px"
                                        />
                                    </FormLayout.Group>
                                </div>
                            )}
                            {activeSection === 'customCSS' && (
                                <div className="custom-css-container">
                                    <TextField
                                        label="Custom CSS"
                                        value={formik.values.customcss}
                                        onChange={(value) => changeTextFn('customcss', value)}
                                        multiline={4}
                                        autoComplete="off"
                                        helpText="Enter your custom CSS here. This will be applied to the currency switcher."
                                    />
                                </div>
                            )}
                        </FormLayout>
                    </Card>
                </Layout.AnnotatedSection>

            </div>
            {toastMarkup}
        </>
    );
};

export default LocationDesignSettings;
